import { Dispatch, SetStateAction, useRef } from 'react'
import { MapPin } from 'lucide-react'
import { Column } from 'src/shared/components/Semantic/all'
import { EmptyState } from 'src/shared/components/EmptyState'
import {
  APPROVED_PROOFS_STATUS,
  DELIVERED_STATUS,
  STARTED_PRODUCTION_STATUS,
  effectiveQuoteStatus,
  SHIPPING_QUOTE_STATUS,
} from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Destinations } from '../../CheckoutTab/components/Destinations'
import { BillingDetails } from '../../CheckoutTab/components/PaymentOptions'
import { CheckoutActionCard } from './CheckoutActionCard'

const canConfirmOrder = (status: string) => {
  const effective = effectiveQuoteStatus(status)
  return (
    effective === APPROVED_PROOFS_STATUS ||
    effective === STARTED_PRODUCTION_STATUS ||
    effective === SHIPPING_QUOTE_STATUS ||
    effective === DELIVERED_STATUS
  )
}

const isOrderPlaced = (status: string) => {
  const effective = effectiveQuoteStatus(status)
  return (
    effective === STARTED_PRODUCTION_STATUS ||
    effective === SHIPPING_QUOTE_STATUS ||
    effective === DELIVERED_STATUS
  )
}

export const ConfirmOrderStep = ({
  quote,
  selectedLocationIds,
  onToggleLocation,
  locationNotesByShippingLocationId,
  onLocationNotesChange,
  onShippingLocationDeleted,
  onBillingReadyChange,
  detailsReady,
  onConfirm,
  isLoading,
}: {
  quote: { id: number; status?: string | null } | null | undefined
  selectedLocationIds: number[]
  onToggleLocation: (id: number) => void
  locationNotesByShippingLocationId: Record<number, string>
  onLocationNotesChange: Dispatch<SetStateAction<Record<number, string>>>
  onShippingLocationDeleted: (locationId: number) => void
  onBillingReadyChange: (ready: boolean) => void
  detailsReady: boolean
  onConfirm: () => void | Promise<void>
  isLoading?: boolean
}) => {
  const { t } = useLocalization()
  const saveBillingRef = useRef<(() => Promise<void>) | null>(null)

  if (!quote) return null

  if (!canConfirmOrder(quote.status ?? '')) {
    return (
      <EmptyState
        icon={<MapPin className="h-9 w-9" />}
        title={t(
          'Proofs need to be approved',
          'Korrekturer behöver godkännas'
        )}
        description={t(
          'Approve the factory proofs first. Then you can confirm billing, shipping, and the order here.',
          'Godkänn korrekturerna först. Sedan kan du bekräfta faktura, leverans och ordern här.'
        )}
        withCard={false}
      />
    )
  }

  const orderPlaced = isOrderPlaced(quote.status ?? '')

  return (
    <Column className="gap-6">
      <p>
        {orderPlaced
          ? t(
              'Billing and shipping details for this order.',
              'Faktura- och leveransuppgifter för den här ordern.'
            )
          : t(
              'Confirm billing and shipping, then start production.',
              'Bekräfta faktura och leverans, sen startar produktionen.'
            )}
      </p>

      <BillingDetails
        onReadyChange={onBillingReadyChange}
        onBindSave={(save) => {
          saveBillingRef.current = save
        }}
      />

      <Destinations
        totalItems={0}
        quote_id={quote.id}
        selectedLocationIds={selectedLocationIds}
        onToggleLocation={onToggleLocation}
        locationNotesByShippingLocationId={locationNotesByShippingLocationId}
        onLocationNotesChange={onLocationNotesChange}
        onShippingLocationDeleted={onShippingLocationDeleted}
      />

      {orderPlaced ? null : (
        <CheckoutActionCard
          title={t(
            'Confirm order & start production',
            'Bekräfta order och starta produktion'
          )}
          description={t(
            'Once confirmed, production begins right away and the order can no longer be changed.',
            'När du bekräftar startar produktionen direkt och ordern kan inte längre ändras.'
          )}
          onClick={() => {
            void (async () => {
              await saveBillingRef.current?.()
              await onConfirm()
            })()
          }}
          isLoading={isLoading}
          disabled={!detailsReady}
        />
      )}
    </Column>
  )
}
